import { CgSpinnerTwo } from '@react-icons/all-files/cg/CgSpinnerTwo'
import { IoIosCheckmarkCircle } from '@react-icons/all-files/io/IoIosCheckmarkCircle'
import { IoIosCloseCircle } from '@react-icons/all-files/io/IoIosCloseCircle'
import { IoStopCircle } from '@react-icons/all-files/io5/IoStopCircle'
import { MdPending } from '@react-icons/all-files/md/MdPending'
import { AgentTaskStatus, CodePlan, Diff } from '~/protos/agents'
import { cm } from '~/utils/utils'
import { CheckIconTwoTone, ErroredSidebarIcon, InputNeededIcon, ProgressCircleIcon } from './icon'

interface TaskStatusIconProps {
  color?: string
  className?: string
  sidebar?: boolean
}

export enum TaskStatusDetailType {
  NONE = 'NONE',
  DOC = 'DOC',
  DIFF = 'DIFF',
  PLAN = 'PLAN',
  INPUT = 'INPUT',
  ENDPOINT_SELECTION = 'ENDPOINT_SELECTION',
  RISK_REVIEW = 'RISK_REVIEW',
  DEPLOY = 'DEPLOY'
}
export interface TaskStatusDetail {
  text?: string
  diffs?: Diff[]
  plan?: CodePlan
  diffComments?: string[]
  endpoints?: import('~/types/migration').MigrationEndpoint[]
}

export interface TypeTaskStatus {
  id: AgentTaskStatus
  group: 'IN_PROGRESS' | 'COMPLETED' | 'NEED_ACTION' | 'ERRORED' | 'STOPPED'
  color: string
  icon: (props: TaskStatusIconProps) => JSX.Element
  detailType: TaskStatusDetailType
  label?: string
}
export type TypeTaskStatuses = {
  [index in AgentTaskStatus]: TypeTaskStatus
}
const StatusColor = {
  InProgress: getComputedStyle(document.documentElement)
    .getPropertyValue('--color-in-progress')
    .trim(),
  Errored: getComputedStyle(document.documentElement).getPropertyValue('--color-errored').trim(),
  Completed: getComputedStyle(document.documentElement)
    .getPropertyValue('--color-completed')
    .trim(),
  NeedAction: getComputedStyle(document.documentElement)
    .getPropertyValue('--color-need-action')
    .trim()
}

// Status → display metadata, grouped by the task family that emits each status.
// Object key order is irrelevant to `buildTaskStatuses` (it iterates entries),
// so these sections exist purely to make "which task does this status belong to"
// obvious. A status shared by several families lives under "Shared lifecycle".
const taskStatuses: { [index in AgentTaskStatus]?: Partial<TypeTaskStatus> } = {
  // ── Shared lifecycle (every task type) ──────────────────────────────────────
  [AgentTaskStatus.NONE_TASK_STATUS]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.INIT]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.SUBMITTED]: { group: 'IN_PROGRESS', detailType: TaskStatusDetailType.DOC },
  [AgentTaskStatus.IN_PROGRESS]: { group: 'IN_PROGRESS', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.AGENT_INTERNAL_STATUS]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.GENERAL_TXT_FEEDBACK_PROVIDED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.COMPLETED]: { group: 'COMPLETED', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.ARCHIVED]: { group: 'COMPLETED', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.ERRORED]: { group: 'ERRORED', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.STOPPED]: { group: 'STOPPED', detailType: TaskStatusDetailType.NONE },

  // ── Planning / Q&A interaction (coding & question-answering tasks) ──────────
  [AgentTaskStatus.PLANNING_COMPLETED]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.PLAN
  },
  [AgentTaskStatus.PLANNING_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.PLAN
  },
  [AgentTaskStatus.PLANNING_REREQUESTED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.STEPS_GENERATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.STEPS_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.QUESTION_ASKED]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.NONE,
    label: 'Input Needed'
  },
  [AgentTaskStatus.QUESTION_ANSWERED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.FINAL_SPECIFICATION_GENERATED]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.FINAL_SPECIFICATION_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },

  // ── Coding / diff review (coder, code review, simple PR, version upgrade…) ──
  [AgentTaskStatus.DIFF_GENERATED]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.DIFF,
    label: 'Code Ready for Review'
  },
  [AgentTaskStatus.DIFF_COMMENT_ADDED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.HUMAN_DIFF_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.DRAFT_GENERATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DIFF
  },

  // ── Document generators (docs, mapping table, diagrams, integration flow…) ──
  [AgentTaskStatus.DOCUMENT_GENERATED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.DOCUMENT_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.IMAGE_GENERATED]: { group: 'COMPLETED', detailType: TaskStatusDetailType.NONE },

  // ── DataWeave / sample-data generators ──────────────────────────────────────
  [AgentTaskStatus.DATAWEAVE_DATA_CREATION_ASST_DATA_TYPE_REQUESTED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },

  // ── MUnit test / Integration test agents ────────────────────────────────────
  [AgentTaskStatus.MUNIT_SCENARIO_GENERATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.MUNIT_DIFF_GENERATED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.INTEGRATION_TEST_DIFF_GENERATED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DIFF
  },

  // ── Migration Accelerator / Mule Refactor / Spring Integration Refactor ─────
  [AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.MIGRATION_API_SPEC_GENERATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.ENDPOINT_SELECTION
  },
  [AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.MIGRATION_AWAITING_RISK_REVIEW]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.RISK_REVIEW,
    label: 'Review risks'
  },
  [AgentTaskStatus.MIGRATION_RISKS_REVIEWED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.RISK_REVIEW,
    label: 'User reviewed risks'
  },
  [AgentTaskStatus.MIGRATION_AWAITING_DEPLOY_INPUT]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.DEPLOY,
    label: 'Deploy: Input Needed'
  },
  [AgentTaskStatus.MIGRATION_DEPLOYING]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DEPLOY,
    label: 'Deploying'
  },
  [AgentTaskStatus.MIGRATION_DEPLOY_INPUT_PROVIDED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DEPLOY,
    label: 'Deploy: Input Provided'
  },
  [AgentTaskStatus.MIGRATION_DEPLOYED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DEPLOY,
    label: 'Deployed'
  },
  [AgentTaskStatus.MIGRATION_DEPLOY_FAILED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DEPLOY,
    label: 'Deploy: Failed'
  },
  [AgentTaskStatus.MIGRATION_CODE_GENERATED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.REFACTOR_USER_NOTES_ADDED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.REFACTOR_API_SPECS_GENERATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.REFACTOR_API_PROPOSED]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.REFACTOR_CODE_GENERATED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.MIGRATION_REFACTOR_ACCELERATOR_DIFF_GENERATED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DIFF
  }
} as const
function buildTaskStatuses() {
  const result = {} as TypeTaskStatuses

  // Add all defined statuses
  Object.entries(taskStatuses).forEach(([key, value]) => {
    const id = Number(key) as TypeTaskStatus['id']
    const status = { ...value, id }
    if (status.icon == null) {
      switch (status.group) {
        case 'COMPLETED':
          status.icon = ({ sidebar, className, color }: TaskStatusIconProps) => {
            return sidebar ? (
              <CheckIconTwoTone className={className} />
            ) : (
              <IoIosCheckmarkCircle
                className={className}
                size={22}
                color={color || StatusColor.Completed}
              />
            )
          }
          break
        case 'ERRORED':
          status.icon = ({ sidebar, className, color }: TaskStatusIconProps) => {
            return sidebar ? (
              <ErroredSidebarIcon className={className} />
            ) : (
              <IoIosCloseCircle
                className={className}
                size={22}
                color={color || StatusColor.Errored}
              />
            )
          }
          break
        case 'NEED_ACTION':
          status.icon = ({ sidebar, color }: TaskStatusIconProps) => {
            return sidebar ? <InputNeededIcon /> : <MdPending size={22} color={color} />
          }
          break
        case 'STOPPED':
          status.icon = (props: TaskStatusIconProps) => (
            <IoStopCircle
              className={props.className}
              size={22}
              color={props.color || StatusColor.Errored}
            />
          )
          break
        default:
          status.icon = ({ sidebar, className, color }: TaskStatusIconProps) => {
            return sidebar ? (
              <ProgressCircleIcon className={cm('animate-spin', className)} />
            ) : (
              <CgSpinnerTwo
                className={cm('animate-spin', className)}
                size={22}
                color={color || StatusColor.InProgress}
              />
            )
          }
          break
      }
    }
    if (status.color == null) {
      switch (status.group) {
        case 'ERRORED':
        case 'STOPPED':
          status.color = StatusColor.Errored
          break
        case 'COMPLETED':
          status.color = StatusColor.Completed
          break
        case 'IN_PROGRESS':
        default:
          status.color = StatusColor.InProgress
      }
    }
    result[id] = status as TypeTaskStatus
  })

  // Add missing statuses with defaults
  Object.values(AgentTaskStatus).forEach(statusValue => {
    if (
      typeof statusValue === 'number' &&
      statusValue !== AgentTaskStatus.UNRECOGNIZED &&
      !result[statusValue]
    ) {
      result[statusValue] = {
        id: statusValue,
        group: 'IN_PROGRESS',
        detailType: TaskStatusDetailType.NONE,
        color: StatusColor.InProgress,
        icon: ({ sidebar, className, color }: TaskStatusIconProps) => {
          return sidebar ? (
            <ProgressCircleIcon className={cm('animate-spin', className)} />
          ) : (
            <CgSpinnerTwo
              className={cm('animate-spin', className)}
              size={22}
              color={color || StatusColor.InProgress}
            />
          )
        }
      }
    }
  })

  return result
}

export const TaskStatuses = buildTaskStatuses()

export const TerminatedTaskStatus = [
  AgentTaskStatus.COMPLETED,
  AgentTaskStatus.ERRORED,
  AgentTaskStatus.DOCUMENT_GENERATED,
  AgentTaskStatus.STOPPED,
  AgentTaskStatus.IMAGE_GENERATED,
  AgentTaskStatus.MIGRATION_CODE_GENERATED,
  AgentTaskStatus.REFACTOR_CODE_GENERATED,
  AgentTaskStatus.MUNIT_DIFF_GENERATED,
  AgentTaskStatus.INTEGRATION_TEST_DIFF_GENERATED,
  AgentTaskStatus.MIGRATION_REFACTOR_ACCELERATOR_DIFF_GENERATED
]

export function isAwaitingUserInput(status: AgentTaskStatus) {
  return AwaitingUserInputStatuses.includes(status)
}

export function isTerminated(status: AgentTaskStatus, isLocalTask?: boolean) {
  if (isLocalTask && status === AgentTaskStatus.DIFF_GENERATED) {
    return true
  }
  return TerminatedTaskStatus.includes(status)
}

export function isSuccessTerminated(status: AgentTaskStatus) {
  return [
    AgentTaskStatus.COMPLETED,
    AgentTaskStatus.DOCUMENT_GENERATED,
    AgentTaskStatus.IMAGE_GENERATED,
    AgentTaskStatus.MIGRATION_CODE_GENERATED,
    AgentTaskStatus.REFACTOR_CODE_GENERATED,
    AgentTaskStatus.MUNIT_DIFF_GENERATED,
    AgentTaskStatus.INTEGRATION_TEST_DIFF_GENERATED,
    AgentTaskStatus.MIGRATION_REFACTOR_ACCELERATOR_DIFF_GENERATED
  ].includes(status)
}

export function isInProgress(status: AgentTaskStatus) {
  return (
    !isTerminated(status) &&
    !isAwaitingUserInput(status) &&
    !FullScreenTaskStatuses.includes(status)
  )
}

export const FullScreenTaskStatuses = [AgentTaskStatus.DIFF_GENERATED]
export const AwaitingUserInputStatuses = [
  AgentTaskStatus.NONE_TASK_STATUS,
  AgentTaskStatus.MIGRATION_AWAITING_RISK_REVIEW,
  AgentTaskStatus.MIGRATION_AWAITING_DEPLOY_INPUT
]

/**
 * Statuses where the diff view should be read-only (no approve/feedback actions).
 * DRAFT_GENERATED: User can only view and download, not approve or provide feedback.
 */
export const ReadOnlyDiffStatuses = [AgentTaskStatus.DRAFT_GENERATED]

export function isReadOnlyDiffStatus(status: AgentTaskStatus) {
  return ReadOnlyDiffStatuses.includes(status)
}

export const isAgentInternalStatus = (status: AgentTaskStatus) => {
  return status === AgentTaskStatus.AGENT_INTERNAL_STATUS
}
